import { RealtimeDbCreateManyValue } from '@/providers/dataProvider/firestoreDataProvider';
import * as Sentry from '@sentry/react';
import { useState } from 'react';
import { useRealtimeDbDataProvider } from './useRealtimeDbDataProvider';
import { useTransformRealtimeDbProps } from './useTransformRealtimeDbProps';

/**
 * Result envelope returned by `batchMutation`.
 *
 * WHY a result instead of a thrown rejection:
 *   Most call sites fire `batchMutation(...)` without `await` and without `.catch()`
 *   (PauseUnpauseButton, BulkAddTime, MakeAnnouncement, MrChat, several realtime
 *   hooks). A thrown rejection at those sites becomes an unhandled promise rejection
 *   in production. Resolving with a discriminated result lets fire-and-forget callers
 *   ignore failures cleanly while still giving awaited callers a way to branch on `ok`.
 *
 * HOW awaited callers MUST use this:
 *   - Check `result.ok` BEFORE treating the write as committed.
 *   - On `!ok`, do NOT advance any "successfully synced" markers / counters
 *     (e.g. the offline-sync watermark in syncOfflineResponsesToFirestore.ts,
 *     the "saved" badge in useRealtimeUserResponses.ts) — otherwise transient
 *     Firestore failures become silent data loss.
 */
export type BatchMutationResult = { ok: true; data: any; meta: any } | { ok: false; error: any };

export const useRealtimeDbBatchMutation = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [metaData, setMetaData] = useState(null);
  const pickedDataProvider = useRealtimeDbDataProvider();

  const transformRealtimeDBProps = useTransformRealtimeDbProps();

  /**
   * Single-attempt batch write.
   *
   * WHY no retry at this layer:
   *   A retry loop here re-fires `createMany` from index 0 when a batch commit
   *   fails mid-way; for values with `createNewDocId: true` every retry mints
   *   fresh auto-IDs at firestoreDataProvider.ts (`doc(collectionRef)`), so
   *   already-committed batches end up duplicated under new IDs. That was the
   *   original APL-10106 partial-commit bug.
   *
   * WHY no thrown rejection on failure:
   *   See BatchMutationResult docblock above.
   *
   * Caller-level recovery options (when needed):
   *   react-query retries on hook consumers, periodic re-sync via
   *   usePeriodicOfflineBackup, manual user retry.
   */
  const batchMutation = async (params: {
    values: RealtimeDbCreateManyValue[];
    meta: any;
  }): Promise<BatchMutationResult> => {
    console.log('batchMutation', params);
    const { values, meta } = params;

    setLoading(true);
    try {
      const { data: updatedData, meta: metaData } = await pickedDataProvider.createMany({
        values,
        meta: {
          ...meta,
          transformRealtimeDBProps,
        },
      });
      setData(updatedData);
      setMetaData(metaData);
      return { ok: true, data: updatedData, meta: metaData };
    } catch (err: any) {
      // Capture-and-resolve instead of re-throw. The hook is the single central
      // place that reports this failure to Sentry; callers don't need to do it
      // again. Awaited callers MUST branch on the returned envelope's `ok` flag —
      // see BatchMutationResult docblock.
      console.error('batchMutation failed:', err);
      setError(err);
      Sentry.captureException(err, {
        extra: {
          context: 'batchMutation failed',
          valueCount: values.length,
        },
      });
      return { ok: false, error: err };
    } finally {
      setLoading(false);
    }
  };
  return {
    loading,
    error,
    batchMutation,
    data,
    meta: metaData,
  };
};
